import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ResponsiveModal } from "@/components/ios/responsive-modal";
import { usePullToRefresh } from "@/hooks/use-pull-to-refresh";
import { Plus, Briefcase, Loader2, Pencil, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useServerFn } from "@tanstack/react-start";
import { logActivity } from "@/lib/activity.functions";
import { useLocaleFmt } from "@/lib/i18n/format";

export const Route = createFileRoute("/_authenticated/app/matters")({
  component: MattersPage,
});

type Matter = {
  id: string;
  name: string;
  number: string | null;
  type: string | null;
  status: string | null;
  value_eur: number | null;
  description: string | null;
};

const EMPTY = { name: "", number: "", type: "", value_eur: "", description: "", status: "open" };

function MattersPage() {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const COLUMNS: { key: string; label: string }[] = [
    { key: "open", label: t("pages.matters.colOpen") },
    { key: "in_progress", label: t("pages.matters.colInProgress") },
    { key: "closed", label: t("pages.matters.colClosed") },
  ];
  const [matters, setMatters] = useState<Matter[] | null>(null);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  const logFn = useServerFn(logActivity);

  const load = async () => {
    const { data, error } = await supabase.from("matters").select("*").order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setMatters((Array.isArray(data) ? data : []) as Matter[]);
  };

  useEffect(() => {
    load();
  }, []);

  const { pull, refreshing } = usePullToRefresh(load, { threshold: 64 });

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (m: Matter) => {
    setEditingId(m.id);
    setForm({
      name: m.name ?? "",
      number: m.number ?? "",
      type: m.type ?? "",
      value_eur: m.value_eur != null ? String(m.value_eur) : "",
      description: m.description ?? "",
      status: m.status ?? "open",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("pages.matters.nameRequired"));
    const payload = {
      name: form.name.trim(),
      number: form.number || null,
      type: form.type || null,
      value_eur: form.value_eur ? Number(form.value_eur) : null,
      status: form.status || "open",
      description: form.description || null,
    };
    if (editingId) {
      const { error } = await supabase.from("matters").update(payload).eq("id", editingId);
      if (error) return toast.error(error.message);
      await logFn({ data: { action: "matter.update", entity: "matter", entity_id: editingId } }).catch(() => {});
      toast.success(t("pages.matters.updated"));
    } else {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const { data: created, error } = await supabase.from("matters").insert({ owner_id: u.user.id, ...payload }).select("id").single();
      if (error) return toast.error(error.message);
      await logFn({ data: { action: "matter.create", entity: "matter", entity_id: created?.id ?? null, metadata: { name: payload.name } } }).catch(() => {});
      toast.success(t("pages.matters.created"));
    }
    setForm(EMPTY);
    setEditingId(null);
    setOpen(false);
    load();
  };

  const remove = async (m: Matter) => {
    if (!confirm(t("pages.matters.deleteConfirm", { name: m.name }))) return;
    const { error } = await supabase.from("matters").delete().eq("id", m.id);
    if (error) return toast.error(error.message);
    await logFn({ data: { action: "matter.delete", entity: "matter", entity_id: m.id, metadata: { name: m.name } } }).catch(() => {});
    toast.success(t("pages.matters.deleted"));
    load();
  };

  const moveStatus = async (id: string, newStatus: string) => {
    const { error } = await supabase.from("matters").update({ status: newStatus }).eq("id", id);
    if (error) return toast.error(error.message);
    await logFn({ data: { action: "matter.status", entity: "matter", entity_id: id, metadata: { status: newStatus } } }).catch(() => {});
    load();
  };

  return (
    <>
      <PageHeader
        title={t("pages.matters.title")}
        description={t("pages.matters.description")}
        actions={
          <Button className="gap-2 ios-tap" onClick={openCreate}>
            <Plus className="h-4 w-4" /> {t("pages.matters.newMatter")}
          </Button>
        }
      />
      {(pull > 0 || refreshing) && (
        <div className="flex justify-center pt-2 text-muted-foreground" style={{ height: Math.max(pull, refreshing ? 32 : 0) }}>
          <Loader2 className={"h-5 w-5 " + (refreshing ? "animate-spin" : "")} />
        </div>
      )}
      <PageBody>
        {matters === null ? (
          <Card className="flex items-center justify-center p-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </Card>
        ) : matters.length === 0 ? (
          <Card className="flex flex-col items-center justify-center p-16 text-center">
            <Briefcase className="mb-4 h-10 w-10 text-muted-foreground" />
            <h3 className="font-display text-xl font-semibold">{t("pages.matters.emptyTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("pages.matters.emptyDesc")}</p>
            <Button className="mt-4 gap-2" onClick={openCreate}>
              <Plus className="h-4 w-4" /> {t("pages.matters.createMatter")}
            </Button>
          </Card>
        ) : (
          <div className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 md:mx-0 md:grid md:grid-cols-3 md:gap-4 md:overflow-visible md:px-0">
            {COLUMNS.map((col) => {
              const items = matters.filter((m) => (m.status ?? "open") === col.key);
              return (
                <div key={col.key} className="w-[85%] shrink-0 snap-center rounded-xl border bg-muted/30 p-3 md:w-auto">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="font-semibold">{col.label}</h3>
                    <span className="rounded-full bg-background px-2 py-0.5 text-xs">{items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {items.map((m) => (
                      <Card key={m.id} className="group p-3 ios-tap relative">
                        <Link to="/app/matters/$matterId" params={{ matterId: m.id }} className="block">
                          <div className="font-medium pr-16">{m.name}</div>
                          <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                            <span>{m.number ?? "-"}</span>
                            {m.value_eur != null && (
                              <span className={cn("font-medium text-foreground")}>
                                {Number(m.value_eur).toLocaleString(localeTag, { style: "currency", currency: "EUR" })}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-foreground/70">
                            {t("pages.matters.openChat")} <ArrowRight className="h-3 w-3" />
                          </div>
                        </Link>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {COLUMNS.filter((c) => c.key !== col.key).map((c) => (
                            <button
                              key={c.key}
                              onClick={() => moveStatus(m.id, c.key)}
                              className="rounded-full border border-border bg-background px-2 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
                            >
                              → {c.label}
                            </button>
                          ))}
                        </div>
                        <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(m)} aria-label={t("pages.matters.edit")}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => remove(m)} aria-label={t("pages.matters.delete")}>
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                    {items.length === 0 && (
                      <div className="rounded-md border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground">
                        {t("pages.matters.noneInColumn")}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </PageBody>

      <ResponsiveModal
        open={open}
        onOpenChange={setOpen}
        title={editingId ? t("pages.matters.modalEdit") : t("pages.matters.modalNew")}
        footer={<Button onClick={save} className="ios-tap">{editingId ? t("pages.matters.save") : t("pages.matters.create")}</Button>}
      >
        <div className="space-y-3">
          <div><Label>{t("pages.matters.fName")} *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("pages.matters.fNumber")}</Label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} /></div>
            <div><Label>{t("pages.matters.fType")}</Label><Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder={t("pages.matters.fTypePh")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("pages.matters.fValue")}</Label><Input type="number" value={form.value_eur} onChange={(e) => setForm({ ...form, value_eur: e.target.value })} /></div>
            <div>
              <Label>{t("pages.matters.fStatus")}</Label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                {COLUMNS.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label>{t("pages.matters.fDescription")}</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
}
